import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Store, RefreshCw, Loader2, Mic, Square, Search, Smartphone, X, Trash2,
  CheckCircle2, Clock, Tag, UserPlus, FileText, Euro, Truck, Wrench, Plus, QrCode, BarChart3, Pencil, Save, Building2, Globe, AlertCircle, MapPin, Calendar, DoorOpen, ListTodo, Phone, CalendarClock, Mail, Upload, Package,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../lib/api";
import StoreInsightStats from "./StoreInsightStats";

const fmtEUR = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0, useGrouping: "always" }).format(n || 0);

export default function StoreInsight() {
  const [overview, setOverview] = useState(null);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("revenue");
  const [selected, setSelected] = useState(null);     // client detail {client, recordings}
  const [categories, setCategories] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);      // recording result for modal
  const [linkOpen, setLinkOpen] = useState(false);
  const [recLink, setRecLink] = useState(null);
  const [view, setView] = useState("clients"); // clients | showroom | actions | stats
  const [showroom, setShowroom] = useState(null);
  const [actions, setActions] = useState([]);
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("si_recent_clients") || "[]"); } catch { return []; }
  });
  const pollRef = useRef(null);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const loadOverview = useCallback(async () => {
    try { const { data } = await api.get("/admin/store-insight/overview"); setOverview(data); } catch { /* */ }
  }, []);
  const loadShowroom = useCallback(async () => {
    try { const { data } = await api.get("/admin/store-insight/showroom"); setShowroom(data); } catch { /* */ }
  }, []);
  const loadActions = useCallback(async () => {
    try { const { data } = await api.get("/admin/store-insight/actions", { params: { status: "open" } }); setActions(data.actions || []); } catch { /* */ }
  }, []);
  const loadClients = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/store-insight/clients", { params: { q, sort } }); setClients(data); }
    catch { toast.error("Errore caricamento schede"); }
    finally { setLoading(false); }
  }, [q, sort]);
  const loadCategories = useCallback(async () => {
    try { const { data } = await api.get("/admin/store-insight/categories"); setCategories(data); } catch { /* */ }
  }, []);
  const loadPending = useCallback(async () => {
    try { const { data } = await api.get("/admin/store-insight/recordings", { params: { status: "pending" } }); setPending(data); } catch { /* */ }
  }, []);

  useEffect(() => { loadOverview(); loadCategories(); loadPending(); loadShowroom(); loadActions(); }, [loadOverview, loadCategories, loadPending, loadShowroom, loadActions]);
  useEffect(() => { const t = setTimeout(loadClients, 250); return () => clearTimeout(t); }, [loadClients]);

  function pushRecent(cl) {
    if (!cl?.id) return;
    const entry = { id: cl.id, name: cl.name, revenue: cl.fic?.revenue || 0, sources: cl.sources || [], attrCount: (cl.attributes || []).length };
    setRecent((prev) => {
      const next = [entry, ...prev.filter((r) => r.id !== cl.id)].slice(0, 6);
      try { localStorage.setItem("si_recent_clients", JSON.stringify(next)); } catch { /* */ }
      return next;
    });
  }

  async function openClient(cid) {
    try { const { data } = await api.get(`/admin/store-insight/clients/${cid}`); setSelected(data); pushRecent(data.client); }
    catch { toast.error("Errore apertura scheda"); }
  }

  async function runSync() {
    try {
      await api.post("/admin/store-insight/sync");
      toast.success("Sincronizzazione avviata (FiC + ARCHI-HUB)…");
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        const { data } = await api.get("/admin/store-insight/overview");
        setOverview(data);
        if (!data.sync?.running) { clearInterval(pollRef.current); pollRef.current = null; loadClients(); toast.success("Schede aggiornate"); }
      }, 4000);
      setTimeout(() => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }, 180000);
    } catch { toast.error("Errore sync"); }
  }

  async function openLink() {
    try { const { data } = await api.get("/admin/store-insight/rec-link"); setRecLink(data); setLinkOpen(true); }
    catch { toast.error("Errore link"); }
  }

  const [recat, setRecat] = useState(false);
  async function runRecategorize() {
    setRecat(true);
    try {
      const { data } = await api.post("/admin/store-insight/recategorize");
      toast.success(`Prodotti ricategorizzati in ${data.categorie?.length || 10} macro-categorie · ${data.updated} schede`);
      loadClients();
      if (selected) openClient(selected.client.id);
    } catch { toast.error("Errore ricategorizzazione"); }
    finally { setRecat(false); }
  }

  const csvInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  async function importCsv(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/store-insight/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import completato: ${data.imported} nuovi, ${data.updated} aggiornati${data.skipped ? `, ${data.skipped} saltati` : ""}`);
      loadClients(); loadOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore importazione CSV");
    } finally { setImporting(false); }
  }

  function toProposal(rec) {
    // normalizza un recording (pending o appena registrato) nella forma del modale
    const ext = rec.extracted || {};
    return {
      recording_id: rec.recording_id || rec.id,
      transcript: rec.transcript,
      attributes: rec.attributes || ext.attributes || [],
      candidates: rec.candidates || [],
      suggested_new_name: rec.suggested_new_name || rec.client_name || ext.client_name || "",
      contact: rec.contact || ext.contact || {},
      admin_name: rec.admin_name || "",
      day: rec.day, time: rec.time,
    };
  }

  async function deletePending(e, id) {
    e.stopPropagation();
    if (!window.confirm("Eliminare questa nota non riconosciuta?")) return;
    try { await api.delete(`/admin/store-insight/recordings/${id}`); toast.success("Nota eliminata"); loadPending(); loadOverview(); }
    catch { toast.error("Errore"); }
  }

  function handleRecResult(r) {
    if (r.kind === "visit") {
      toast.success(`Accesso show room registrato${r.request_type ? ` · ${r.request_type}` : ""}`);
      loadShowroom(); loadOverview(); loadActions();
      return;
    }
    setProposal(toProposal(r));
  }

  // Registrazioni avviate dall'assistente AI: aprono qui la scheda di verifica.
  const recHandlerRef = useRef();
  recHandlerRef.current = handleRecResult;
  useEffect(() => {
    const consume = () => {
      try {
        const raw = sessionStorage.getItem("si_pending_recording");
        if (raw) { sessionStorage.removeItem("si_pending_recording"); recHandlerRef.current?.(JSON.parse(raw)); return true; }
      } catch { /* */ }
      return false;
    };
    consume(); // montaggio: arrivo da un'altra tab/pagina
    const onEvt = (e) => { if (!consume() && e.detail) recHandlerRef.current?.(e.detail); };
    window.addEventListener("si:open-recording", onEvt);
    return () => window.removeEventListener("si:open-recording", onEvt);
  }, []);

  async function afterConfirm() {
    setProposal(null);
    await Promise.all([loadClients(), loadOverview(), loadCategories(), loadPending(), loadActions(), loadShowroom()]);
    if (selected) openClient(selected.client.id);
  }

  return (
    <div className="mt-8" data-testid="store-insight">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-ink inline-flex items-center gap-2"><Store size={20} /> Store Insight</h2>
          <p className="text-sm text-slate1 mt-1">Schede cliente da Fatture in Cloud + ARCHI-HUB, arricchite con note vocali AI.</p>
        </div>
        <div className="flex items-center gap-2">
          <RecorderButton onResult={handleRecResult} />
          <button onClick={openLink} className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-ink/20 text-xs tracking-[0.15em] uppercase text-ink hover:bg-cream2" data-testid="si-link-btn">
            <Smartphone size={14} /> Link cellulare
          </button>
          <input ref={csvInputRef} type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" data-testid="si-import-input" />
          <button onClick={() => csvInputRef.current?.click()} disabled={importing} className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-ink/20 text-xs tracking-[0.15em] uppercase text-ink hover:bg-cream2 disabled:opacity-50" data-testid="si-import-btn" title="Importa anagrafica clienti da file CSV (senza duplicati)">
            {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Importa CSV
          </button>
          <button onClick={runRecategorize} disabled={recat} className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-ink/20 text-xs tracking-[0.15em] uppercase text-ink hover:bg-cream2 disabled:opacity-50" data-testid="si-recat-btn" title="Riporta i prodotti nelle 10 macro-categorie (dai dati già sincronizzati)">
            {recat ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />} Ricategorizza
          </button>
          <button onClick={runSync} disabled={overview?.sync?.running} className="inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-ink text-cream text-xs tracking-[0.15em] uppercase disabled:opacity-50" data-testid="si-sync-btn">
            {overview?.sync?.running ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sincronizza
          </button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Kpi label="Schede cliente" value={overview?.clients ?? "—"} icon={<Store size={16} />} testid="si-kpi-clients" />
        <Kpi label="Note da confermare" value={overview?.pending_recordings ?? "—"} icon={<Clock size={16} />} testid="si-kpi-pending" highlight={overview?.pending_recordings > 0} />
        <Kpi label="Note totali" value={overview?.recordings ?? "—"} icon={<Mic size={16} />} testid="si-kpi-recordings" />
        <Kpi label="Accessi showroom" value={overview?.visits ?? "—"} icon={<DoorOpen size={16} />} testid="si-kpi-visits" />
        <Kpi label="Azioni da fare" value={overview?.open_actions ?? "—"} icon={<ListTodo size={16} />} testid="si-kpi-actions" highlight={overview?.open_actions > 0} />
        <Kpi label="Categorie dati" value={overview?.categories ?? "—"} icon={<Tag size={16} />} testid="si-kpi-categories" />
      </div>

      {/* Toggle vista */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setView("clients")} className={`px-4 py-2 rounded-sm text-xs tracking-[0.15em] uppercase border ${view === "clients" ? "bg-ink text-cream border-ink" : "border-ink/20 text-ink"}`} data-testid="si-view-clients">Schede clienti</button>
        <button onClick={() => setView("showroom")} className={`px-4 py-2 rounded-sm text-xs tracking-[0.15em] uppercase border inline-flex items-center gap-1.5 ${view === "showroom" ? "bg-ink text-cream border-ink" : "border-ink/20 text-ink"}`} data-testid="si-view-showroom"><DoorOpen size={14} /> Accesso show room</button>
        <button onClick={() => setView("actions")} className={`px-4 py-2 rounded-sm text-xs tracking-[0.15em] uppercase border inline-flex items-center gap-1.5 ${view === "actions" ? "bg-ink text-cream border-ink" : "border-ink/20 text-ink"}`} data-testid="si-view-actions"><ListTodo size={14} /> Azioni {actions.length > 0 && <span className="bg-gold text-ink rounded-full px-1.5 py-0.5 text-[10px] leading-none">{actions.length}</span>}</button>
        <button onClick={() => setView("stats")} className={`px-4 py-2 rounded-sm text-xs tracking-[0.15em] uppercase border inline-flex items-center gap-1.5 ${view === "stats" ? "bg-ink text-cream border-ink" : "border-ink/20 text-ink"}`} data-testid="si-view-stats"><BarChart3 size={14} /> Statistiche</button>
      </div>

      {view === "stats" ? <StoreInsightStats /> :
       view === "showroom" ? <ShowroomView data={showroom} onReload={() => { loadShowroom(); loadOverview(); }} /> :
       view === "actions" ? <ActionsView actions={actions} onReload={() => { loadActions(); loadOverview(); }} /> : <></>}
      {view === "clients" && <>

      {/* Note da confermare */}
      {pending.length > 0 && (
        <div className="mb-6 border border-amber-300 bg-amber-50/60 rounded-sm p-4" data-testid="si-pending-list">
          <div className="text-[11px] tracking-[0.18em] uppercase text-amber-800 font-bold mb-3 inline-flex items-center gap-2"><Clock size={13} /> Note vocali da confermare ({pending.length})</div>
          <div className="space-y-2">
            {pending.map((r) => (
              <div key={r.id} className="w-full bg-white border border-amber-200 rounded-sm px-3 py-2 hover:border-amber-400 transition-colors flex items-start gap-2" data-testid={`si-pending-${r.id}`}>
                <button onClick={() => setProposal(toProposal(r))} className="flex-1 text-left min-w-0" data-testid={`si-pending-open-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-ink truncate">{r.suggested_new_name || (r.candidates?.[0]?.name) || "Cliente da identificare"}</span>
                    <span className="text-[10px] text-slate1 shrink-0">{r.day} · {r.time}{r.admin_name ? ` · ${r.admin_name}` : ""} · {r.source === "mobile" ? "da cellulare" : "da PC"}</span>
                  </div>
                  <div className="text-xs text-slate1 truncate mt-0.5">{r.transcript}</div>
                </button>
                <button onClick={(e) => deletePending(e, r.id)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-sm shrink-0" title="Elimina nota" data-testid={`si-pending-del-${r.id}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Lista clienti */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente…" className="w-full pl-9 pr-3 py-2 border border-ink/20 rounded-sm text-sm bg-white" data-testid="si-search" />
            </div>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-ink/20 rounded-sm text-xs py-2 px-2 bg-white" data-testid="si-sort">
              <option value="revenue">Fatturato</option>
              <option value="name">Nome</option>
              <option value="recent">Aggiornati</option>
            </select>
          </div>
          <div className="space-y-1.5 max-h-[70vh] overflow-auto pr-1">
            {loading && <div className="text-sm text-slate1 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Carico…</div>}
            {!loading && clients.length === 0 && <div className="text-sm text-slate1 py-8 text-center">Nessuna scheda. Premi <b>Sincronizza</b> per importare i clienti da Fatture in Cloud e ARCHI-HUB.</div>}
            {clients.map((c) => (
              <button key={c.id} onClick={() => openClient(c.id)} className={`w-full text-left px-3 py-2.5 rounded-sm border transition-colors ${selected?.client?.id === c.id ? "border-ink bg-cream2" : "border-ink/10 hover:border-ink/30 bg-white"}`} data-testid={`si-client-${c.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-ink truncate">{c.name}</span>
                  {(c.fic?.revenue > 0) && <span className="text-xs text-gold tabular-nums shrink-0">{fmtEUR(c.fic.revenue)}</span>}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px] text-slate1">
                  {(c.sources || []).map((s) => <span key={s} className="uppercase tracking-wider bg-cream2 px-1.5 py-0.5 rounded">{s === "fic" ? "FiC" : s === "archihub" ? "Hub" : s === "voice" ? "Voce" : s === "manual" ? "Manuale" : s}</span>)}
                  {c.attributes?.length > 0 && <span className="inline-flex items-center gap-0.5"><Tag size={9} /> {c.attributes.length}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Dettaglio */}
        <div className="lg:col-span-3">
          {!selected ? (
            recent.length > 0 ? (
              <div data-testid="si-recent-grid">
                <div className="text-[11px] tracking-[0.18em] uppercase text-slate1 mb-3 inline-flex items-center gap-2"><Clock size={13} /> Ultime schede viste</div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {recent.map((r) => (
                    <button key={r.id} onClick={() => openClient(r.id)} className="text-left p-3 rounded-sm border border-ink/10 bg-white hover:border-ink/40 hover:shadow-sm transition-all min-h-[96px] flex flex-col justify-between" data-testid={`si-recent-${r.id}`}>
                      <div className="text-sm font-medium text-ink line-clamp-2 leading-snug">{r.name}</div>
                      <div className="mt-2">
                        {r.revenue > 0 && <div className="text-xs text-gold tabular-nums mb-1">{fmtEUR(r.revenue)}</div>}
                        <div className="flex items-center gap-1 flex-wrap text-[9px] text-slate1">
                          {(r.sources || []).map((s) => <span key={s} className="uppercase tracking-wider bg-cream2 px-1.5 py-0.5 rounded">{s === "fic" ? "FiC" : s === "archihub" ? "Hub" : s === "voice" ? "Voce" : s === "manual" ? "Manuale" : s === "csv" ? "CSV" : s}</span>)}
                          {r.attrCount > 0 && <span className="inline-flex items-center gap-0.5"><Tag size={9} /> {r.attrCount}</span>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="border border-dashed border-ink/15 rounded-sm p-5 text-center text-slate1 text-xs mt-3">
                  Cerca e clicca un cliente per aprire la scheda completa qui.
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-ink/20 rounded-sm p-10 text-center text-slate1 text-sm" data-testid="si-detail-empty">
                Seleziona un cliente per vedere la scheda completa.
              </div>
            )
          ) : (
            <ClientDetail data={selected} categories={categories} onClose={() => setSelected(null)} onChanged={() => openClient(selected.client.id)} onDeleted={() => { setSelected(null); loadClients(); }} />
          )}
        </div>
      </div>

      {/* Categorie scoperte */}
      {categories.length > 0 && (
        <div className="mt-8" data-testid="si-categories">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate1 mb-2">Categorie dati scoperte dall'AI</div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <span key={c.key} className="inline-flex items-center gap-1.5 text-xs bg-cream2 border border-ink/10 rounded-full px-3 py-1 text-ink">
                {c.label} <span className="text-slate1">{c.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      </>}

      {proposal && <ProposalModal proposal={proposal} categories={categories} onClose={() => setProposal(null)} onConfirmed={afterConfirm} />}
      {linkOpen && recLink && <LinkModal link={recLink} onClose={() => setLinkOpen(false)} onRotate={async () => { const { data } = await api.post("/admin/store-insight/rec-link/rotate"); setRecLink(data); toast.success("Nuovo link generato"); }} />}
    </div>
  );
}

function Kpi({ label, value, icon, testid, highlight }) {
  return (
    <div className={`rounded-sm border p-3 ${highlight ? "border-amber-300 bg-amber-50" : "border-ink/10 bg-white"}`} data-testid={testid}>
      <div className="text-[10px] tracking-[0.15em] uppercase text-slate1 inline-flex items-center gap-1.5">{icon} {label}</div>
      <div className="text-2xl font-semibold text-ink mt-1 tabular-nums">{value}</div>
    </div>
  );
}

/* ---------------- Accesso Show Room ---------------- */
function ShowroomView({ data, onReload }) {
  const [editing, setEditing] = useState(null);
  if (!data) return <div className="text-sm text-slate1 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Carico…</div>;
  const visits = data.visits || [];
  const s = data.summary || {};
  async function del(id) {
    if (!window.confirm("Eliminare questo accesso?")) return;
    try { await api.delete(`/admin/store-insight/showroom/${id}`); toast.success("Accesso eliminato"); onReload(); }
    catch { toast.error("Errore"); }
  }
  return (
    <div data-testid="si-showroom-view">
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Kpi label="Accessi totali" value={s.total ?? 0} icon={<DoorOpen size={16} />} testid="si-sr-total" />
        <Kpi label="Ultimi 7 giorni" value={s.last_7_days ?? 0} icon={<Calendar size={16} />} testid="si-sr-week" />
        <Kpi label="Ultimi 30 giorni" value={s.last_30_days ?? 0} icon={<Calendar size={16} />} testid="si-sr-month" />
      </div>
      <p className="text-sm text-slate1 mb-4">Note vocali registrate <b>senza un nominativo cliente</b>: ogni visita in showroom viene tracciata con tipo di richiesta, giorno e ora. Usa la matita per correggere eventuali errori di trascrizione. Per i flussi settimanali/mensili e le categorie vai su <b>Statistiche</b>.</p>
      {visits.length === 0 ? (
        <div className="border border-dashed border-ink/20 rounded-sm p-10 text-center text-slate1 text-sm" data-testid="si-showroom-empty">
          Nessun accesso registrato. Registra una nota vocale senza indicare il nominativo del cliente per aggiungere un accesso allo show room.
        </div>
      ) : (
        <div className="space-y-2">
          {visits.map((v) => (
            <div key={v.id} className="bg-white border border-ink/10 rounded-sm px-4 py-3" data-testid={`si-visit-${v.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {v.request_type ? <span className="text-xs tracking-wider uppercase bg-gold/20 text-gold rounded-full px-2.5 py-0.5">{v.request_type}</span> : <span className="text-xs text-slate1 italic">richiesta non specificata</span>}
                    <span className="text-[10px] text-slate1">{v.day} · {v.time}{v.admin_name ? ` · ${v.admin_name}` : ""} · {v.source === "mobile" ? "da cellulare" : "da PC"}</span>
                  </div>
                  <div className="text-sm text-ink/80 mt-1">{v.transcript}</div>
                  {(v.attributes || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {v.attributes.map((a, i) => <span key={i} className="text-[11px] bg-cream2 border border-ink/10 rounded px-2 py-0.5 text-ink"><span className="text-slate1 uppercase tracking-wider mr-1">{a.category}:</span>{a.value}</span>)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setEditing(v)} className="p-1.5 text-ink hover:bg-cream2 rounded-sm" title="Modifica accesso" data-testid={`si-visit-edit-${v.id}`}><Pencil size={14} /></button>
                  <button onClick={() => del(v.id)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-sm" data-testid={`si-visit-del-${v.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <VisitEditModal visit={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onReload(); }} />}
    </div>
  );
}

function VisitEditModal({ visit, onClose, onSaved }) {
  const [requestType, setRequestType] = useState(visit.request_type || "");
  const [transcript, setTranscript] = useState(visit.transcript || "");
  const [attrs, setAttrs] = useState(() => (visit.attributes || []).map((a) => ({ ...a })));
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.put(`/admin/store-insight/showroom/${visit.id}`, {
        request_type: requestType.trim(),
        transcript: transcript.trim(),
        attributes: attrs.filter((a) => (a.category || "").trim() && (a.value || "").trim()).map((a) => ({ category: a.category, value: a.value })),
      });
      toast.success("Accesso aggiornato");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="si-visit-edit-modal">
      <div className="bg-cream w-full max-w-xl max-h-[90vh] overflow-auto rounded-sm shadow-2xl">
        <div className="sticky top-0 bg-cream border-b border-ink/10 px-5 py-3 flex items-center justify-between">
          <div className="text-sm tracking-[0.18em] uppercase text-ink font-semibold">Modifica accesso show room</div>
          <button onClick={onClose} className="p-1 text-slate1 hover:text-ink" data-testid="si-visit-edit-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-[10px] tracking-[0.15em] uppercase text-slate1">Tipo di richiesta</span>
            <input value={requestType} onChange={(e) => setRequestType(e.target.value)} placeholder="es. Cucina, Preventivo, Informazioni…" className="mt-1 w-full px-3 py-2 border border-ink/20 rounded-sm text-sm bg-white" data-testid="si-visit-edit-type" />
          </label>
          <label className="block">
            <span className="text-[10px] tracking-[0.15em] uppercase text-slate1">Trascrizione</span>
            <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} rows={4} className="mt-1 w-full px-3 py-2 border border-ink/20 rounded-sm text-sm bg-white resize-y" data-testid="si-visit-edit-transcript" />
          </label>
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 mb-2">Dati raccolti</div>
            <div className="space-y-1.5">
              {attrs.map((a, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-sm border border-ink/15 bg-white" data-testid={`si-visit-attr-${i}`}>
                  <input value={a.category} onChange={(e) => setAttrs((p) => p.map((x, j) => j === i ? { ...x, category: e.target.value } : x))} placeholder="Categoria" className="w-40 shrink-0 text-xs tracking-wider uppercase text-gold bg-transparent border-b border-dashed border-ink/20 focus:outline-none" />
                  <input value={a.value} onChange={(e) => setAttrs((p) => p.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} placeholder="Valore" className="flex-1 text-sm text-ink bg-transparent border-b border-dashed border-ink/20 focus:outline-none" />
                  <button onClick={() => setAttrs((p) => p.filter((_, j) => j !== i))} className="p-1 text-slate1 hover:text-rose-600"><X size={13} /></button>
                </div>
              ))}
            </div>
            <button onClick={() => setAttrs((p) => [...p, { category: "", value: "" }])} className="mt-2 text-xs text-ink inline-flex items-center gap-1 hover:underline" data-testid="si-visit-attr-add"><Plus size={12} /> Aggiungi dato</button>
          </div>
        </div>
        <div className="sticky bottom-0 bg-cream border-t border-ink/10 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate1 hover:text-ink">Annulla</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-sm bg-ink text-cream text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2 disabled:opacity-50" data-testid="si-visit-edit-save">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salva</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Azioni suggerite dall'AI ---------------- */
function ActionsView({ actions, onReload }) {
  const icon = (t) => {
    const k = (t || "").toLowerCase();
    if (k.includes("chiam") || k.includes("call")) return <Phone size={14} />;
    if (k.includes("appunt")) return <CalendarClock size={14} />;
    if (k.includes("mail") || k.includes("email")) return <Mail size={14} />;
    return <ListTodo size={14} />;
  };
  async function done(id) {
    try { await api.post(`/admin/store-insight/actions/${id}/done`); toast.success("Azione completata"); onReload(); }
    catch { toast.error("Errore"); }
  }
  async function del(id) {
    try { await api.delete(`/admin/store-insight/actions/${id}`); onReload(); }
    catch { toast.error("Errore"); }
  }
  return (
    <div data-testid="si-actions-view">
      <p className="text-sm text-slate1 mb-4">Azioni consigliate dall'AI a partire dalle note registrate: call to action, clienti da richiamare e appuntamenti da rifissare. Ogni lunedì ricevi via email il riepilogo settimanale con queste azioni.</p>
      {actions.length === 0 ? (
        <div className="border border-dashed border-ink/20 rounded-sm p-10 text-center text-slate1 text-sm" data-testid="si-actions-empty">
          Nessuna azione in sospeso. 🎉 Le nuove note vocali genereranno automaticamente le azioni consigliate.
        </div>
      ) : (
        <div className="space-y-2">
          {actions.map((a) => (
            <div key={a.id} className="bg-white border border-ink/10 rounded-sm px-4 py-3 flex items-start gap-3" data-testid={`si-action-${a.id}`}>
              <span className="mt-0.5 w-7 h-7 rounded-full bg-cream2 text-ink inline-flex items-center justify-center shrink-0">{icon(a.type)}</span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-ink">{a.title}</div>
                {a.detail && <div className="text-xs text-slate1 mt-0.5">{a.detail}</div>}
                <div className="text-[10px] text-slate1 mt-1">
                  {a.client_name ? <span className="font-medium text-ink">{a.client_name}</span> : null}
                  {a.day ? ` · ${a.day}` : ""}{a.type ? ` · ${a.type}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => done(a.id)} className="px-2.5 py-1.5 rounded-sm bg-ink text-cream text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1" data-testid={`si-action-done-${a.id}`}><CheckCircle2 size={12} /> Fatto</button>
                <button onClick={() => del(a.id)} className="p-1.5 text-slate1 hover:text-rose-600" data-testid={`si-action-del-${a.id}`}><X size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Recorder (desktop) ---------------- */
function RecorderButton({ onResult }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null); const chunksRef = useRef([]); const streamRef = useRef(null); const timerRef = useRef(null);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const mr = new MediaRecorder(stream, MediaRecorder.isTypeSupported(mime) ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => upload(new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" }));
      mediaRef.current = mr; mr.start(); setRecording(true); setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch { toast.error("Microfono non disponibile"); }
  }
  function stop() {
    if (mediaRef.current && recording) {
      mediaRef.current.stop(); setRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    }
  }
  async function upload(blob) {
    setUploading(true);
    try {
      const fd = new FormData();
      const ext = (blob.type || "").includes("mp4") ? "mp4" : "webm";
      fd.append("audio", blob, `nota.${ext}`);
      const { data } = await api.post("/admin/store-insight/record", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore trascrizione"); }
    finally { setUploading(false); }
  }

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  if (recording) {
    return (
      <button onClick={stop} className="inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-rose-600 text-white text-xs tracking-[0.15em] uppercase shadow-lg" data-testid="si-rec-stop">
        <span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
        <span className="font-mono tabular-nums">{mm}:{ss}</span>
        <Square size={13} fill="currentColor" /> Ferma
      </button>
    );
  }
  return <button onClick={start} disabled={uploading} className="inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-gold text-ink text-xs tracking-[0.15em] uppercase disabled:opacity-50" data-testid="si-rec-start">{uploading ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />} {uploading ? "Trascrivo…" : "Nota vocale"}</button>;
}

/* ---------------- Dettaglio cliente (modificabile) ---------------- */
function ClientDetail({ data, categories = [], onChanged, onDeleted, onClose }) {
  const c = data.client;
  const isEmailCat = (x) => /mail/i.test((x || "").trim());
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [attrs, setAttrs] = useState([]);
  const [saving, setSaving] = useState(false);
  const grouped = {};
  for (const a of (c.attributes || [])) { (grouped[a.category] = grouped[a.category] || []).push(a); }
  const geo = c.geo || {};

  function startEdit() {
    setForm({
      name: c.name || "", vat: c.vat || "", tax_code: c.tax_code || "", type: c.type || (c.vat ? "ditta" : "privato"),
      is_export: !!c.is_export, email: c.email || "", phone: c.phone || "", address: c.address || "",
      city: geo.city || "", province: geo.province || "", country: geo.country || "",
    });
    setAttrs((c.attributes || []).map((a) => ({ category: a.category || "", value: a.value || "", source: a.source || "manual" })));
    setEditing(true);
  }
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Categorie già censite non ancora presenti tra i dati raccolti → tocco per aggiungere riga
  const usedCats = new Set(attrs.map((a) => (a.category || "").trim().toLowerCase()));
  const suggestCats = (categories || []).filter((x) => x.label && !isEmailCat(x.label) && !usedCats.has(x.label.trim().toLowerCase()));

  async function save() {
    setSaving(true);
    try {
      const attributes = attrs
        .filter((a) => (a.category || "").trim() && (a.value || "").trim())
        .map((a) => ({ category: a.category.trim(), value: a.value.trim(), source: a.source || "manual" }));
      await api.put(`/admin/store-insight/clients/${c.id}`, { ...form, attributes });
      toast.success("Scheda aggiornata");
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  }

  async function del() {
    if (!window.confirm(`Eliminare la scheda di "${c.name}"?`)) return;
    try { await api.delete(`/admin/store-insight/clients/${c.id}`); toast.success("Scheda eliminata"); onDeleted(); }
    catch { toast.error("Errore"); }
  }

  const isDitta = (c.type || (c.vat ? "ditta" : "privato")) === "ditta";

  if (editing) {
    return (
      <div className="border border-ink/10 rounded-sm bg-white p-5" data-testid="si-detail-edit">
        <div className="text-[11px] tracking-[0.18em] uppercase text-slate1 mb-3">Modifica scheda</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nome"><input value={form.name} onChange={(e) => setF("name", e.target.value)} className="si-input" data-testid="si-edit-name" /></Field>
          <Field label="Tipo">
            <select value={form.type} onChange={(e) => setF("type", e.target.value)} className="si-input" data-testid="si-edit-type">
              <option value="ditta">Ditta</option><option value="privato">Privato</option>
            </select>
          </Field>
          <Field label="P.IVA"><input value={form.vat} onChange={(e) => setF("vat", e.target.value)} className="si-input" data-testid="si-edit-vat" /></Field>
          <Field label="Codice Fiscale"><input value={form.tax_code} onChange={(e) => setF("tax_code", e.target.value)} className="si-input" data-testid="si-edit-tax-code" /></Field>
          <Field label="Email"><input value={form.email} onChange={(e) => setF("email", e.target.value)} className="si-input" data-testid="si-edit-email" /></Field>
          <Field label="Telefono"><input value={form.phone} onChange={(e) => setF("phone", e.target.value)} className="si-input" data-testid="si-edit-phone" /></Field>
          <Field label="Indirizzo"><input value={form.address} onChange={(e) => setF("address", e.target.value)} className="si-input" data-testid="si-edit-address" /></Field>
          <Field label="Città"><input value={form.city} onChange={(e) => setF("city", e.target.value)} className="si-input" data-testid="si-edit-city" /></Field>
          <Field label="Provincia"><input value={form.province} onChange={(e) => setF("province", e.target.value)} className="si-input" data-testid="si-edit-province" /></Field>
          <Field label="Stato"><input value={form.country} onChange={(e) => setF("country", e.target.value)} placeholder="es. Italia" className="si-input" data-testid="si-edit-country" /></Field>
        </div>
        <label className="flex items-center gap-2 mt-3 text-sm text-ink"><input type="checkbox" checked={form.is_export} onChange={(e) => setF("is_export", e.target.checked)} data-testid="si-edit-export" /> Cliente Export (fatture IVA 0%)</label>

        {/* Dati raccolti per categoria (modificabili) */}
        <div className="mt-5" data-testid="si-edit-attrs">
          <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 mb-2">Dati raccolti nelle categorie</div>
          {attrs.length === 0 && <div className="text-sm text-slate-400 mb-1">Nessun dato. Aggiungilo qui sotto o tocca una categoria censita.</div>}
          <div className="space-y-1.5">
            {attrs.map((a, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-sm border border-ink/15 bg-white" data-testid={`si-edit-attr-row-${i}`}>
                <input value={a.category} onChange={(e) => setAttrs((p) => p.map((x, j) => j === i ? { ...x, category: e.target.value } : x))} placeholder="Categoria" className="w-40 shrink-0 text-xs tracking-wider uppercase text-gold bg-transparent border-b border-dashed border-ink/20 focus:outline-none" data-testid={`si-edit-attr-cat-${i}`} />
                <input value={a.value} onChange={(e) => setAttrs((p) => p.map((x, j) => j === i ? { ...x, value: e.target.value, source: "manual" } : x))} placeholder="Valore" className="flex-1 text-sm text-ink bg-transparent border-b border-dashed border-ink/20 focus:outline-none" data-testid={`si-edit-attr-val-${i}`} />
                <button type="button" onClick={() => setAttrs((p) => p.filter((_, j) => j !== i))} className="p-1 text-rose-600 hover:bg-rose-50 rounded-sm shrink-0" title="Rimuovi" data-testid={`si-edit-attr-del-${i}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setAttrs((p) => [...p, { category: "", value: "", source: "manual" }])} className="mt-2 text-xs text-ink inline-flex items-center gap-1 hover:underline" data-testid="si-edit-attr-add"><Plus size={12} /> Aggiungi dato</button>
          {suggestCats.length > 0 && (
            <div className="mt-3" data-testid="si-edit-cat-suggestions">
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 mb-1.5">Categorie già censite — tocca per compilare</div>
              <div className="flex flex-wrap gap-1.5">
                {suggestCats.map((x) => (
                  <button type="button" key={x.key || x.label} onClick={() => setAttrs((p) => [...p, { category: x.label, value: "", source: "manual" }])} className="inline-flex items-center gap-1 text-[11px] text-ink border border-ink/20 bg-white hover:border-gold hover:bg-gold/10 rounded-full px-2.5 py-1 transition-colors" data-testid={`si-edit-cat-chip-${x.key || x.label}`}>
                    <Plus size={10} /> {x.label}{x.count ? <span className="text-slate1">· {x.count}</span> : null}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm text-slate1 hover:text-ink">Annulla</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-sm bg-ink text-cream text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2 disabled:opacity-50" data-testid="si-edit-save">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salva</button>
        </div>
        <style>{`.si-input{width:100%;border:1px solid rgba(43,42,40,.2);border-radius:2px;padding:8px 10px;font-size:14px;background:#fff}`}</style>
      </div>
    );
  }

  return (
    <div className="border border-ink/10 rounded-sm bg-white p-5" data-testid="si-detail">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold text-ink">{c.name}</h3>
            <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${isDitta ? "bg-ink/10 text-ink" : "bg-cream2 text-slate1"}`} data-testid="si-badge-type">
              {isDitta ? <><Building2 size={10} /> Ditta</> : "Privato"}
            </span>
            {c.is_export && <span className="text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full bg-gold/20 text-gold inline-flex items-center gap-1" data-testid="si-badge-export"><Globe size={10} /> Export</span>}
          </div>
          <div className="text-xs text-slate1 mt-1.5 space-y-0.5">
            {c.vat && <div>P.IVA: {c.vat}</div>}
            {c.tax_code && <div>C.F.: {c.tax_code}</div>}
            {c.phone && <div>Tel: {c.phone}</div>}
            {c.email && <div>Email: {c.email}</div>}
            {c.address && <div>{c.address}</div>}
            {(geo.city || geo.province || geo.country) && <div className="inline-flex items-center gap-1 text-ink"><MapPin size={11} /> {[geo.city, geo.province, geo.country].filter(Boolean).join(", ")}</div>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onClose && <button onClick={onClose} className="p-2 text-slate1 hover:bg-cream2 rounded-sm" title="Torna alle ultime schede" data-testid="si-detail-close"><X size={16} /></button>}
          <button onClick={startEdit} className="p-2 text-ink hover:bg-cream2 rounded-sm" title="Modifica" data-testid="si-detail-edit-btn"><Pencil size={15} /></button>
          <button onClick={del} className="p-2 text-rose-600 hover:bg-rose-50 rounded-sm" data-testid="si-detail-del"><Trash2 size={16} /></button>
        </div>
      </div>

      {/* Metriche */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
        <Metric icon={<Euro size={14} />} label="Fatturato" value={c.fic?.revenue ? fmtEUR(c.fic.revenue) : "—"} />
        <Metric icon={<FileText size={14} />} label="Fatture" value={c.fic?.invoices ?? "—"} />
        <Metric icon={<AlertCircle size={14} />} label="Insoluti" value={c.fic?.outstanding ? fmtEUR(c.fic.outstanding) : "—"} alert={c.fic?.outstanding > 0} />
        <Metric icon={<Truck size={14} />} label="Consegne" value={c.archihub?.deliveries ?? "—"} />
        <Metric icon={<Wrench size={14} />} label="Assistenze" value={c.archihub?.assistances ?? "—"} />
      </div>
      {c.fic?.last_invoice_date && <div className="text-[11px] text-slate1 mt-2">Ultima fattura: {c.fic.last_invoice_date}</div>}

      {/* Tipologie prodotti (macro-categorie) */}
      {(c.fic?.categories || []).length > 0 && (
        <div className="mt-5" data-testid="si-categories-breakdown">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate1 mb-2 inline-flex items-center gap-2"><Package size={13} /> Tipologie prodotti vendute</div>
          <div className="space-y-2">
            {c.fic.categories.map((cat, i) => {
              const max = Math.max(...c.fic.categories.map((x) => x.amount || 0), 1);
              const pct = Math.max(3, Math.round(((cat.amount || 0) / max) * 100));
              return (
                <div key={i} data-testid={`si-cat-${i}`}>
                  <div className="flex items-center justify-between text-sm mb-0.5">
                    <span className="text-ink">{cat.category}{cat.qty ? <span className="text-slate1 text-xs ml-1">· {cat.qty} pz</span> : null}</span>
                    <span className="text-slate1 tabular-nums shrink-0">{fmtEUR(cat.amount)}</span>
                  </div>
                  <div className="h-1.5 bg-cream2 rounded-full overflow-hidden"><div className="h-full bg-gold rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Prodotti acquistati */}
      {(c.fic?.products || []).length > 0 && (
        <div className="mt-5" data-testid="si-products">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate1 mb-2">Prodotti acquistati</div>
          <div className="space-y-1">
            {c.fic.products.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-ink/5 py-1">
                <span className="text-ink truncate pr-2">{p.name}</span>
                <span className="text-slate1 shrink-0 tabular-nums">{p.qty ? `${p.qty}× · ` : ""}{fmtEUR(p.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Appuntamenti dal calendario */}
      {data.appointments?.count > 0 && (
        <div className="mt-5" data-testid="si-appointments">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate1 mb-2 inline-flex items-center gap-2">
            <Calendar size={13} /> Appuntamenti in calendario
            <span className="bg-ink text-cream rounded-full px-2 py-0.5 text-[10px]" data-testid="si-appt-count">{data.appointments.count}</span>
            {data.appointments.upcoming > 0 && <span className="text-emerald-700 normal-case">({data.appointments.upcoming} futuri)</span>}
          </div>
          <div className="space-y-1">
            {data.appointments.items.map((ev, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-ink/5 py-1">
                <span className="text-ink truncate pr-2">{ev.summary || "(senza titolo)"}</span>
                <span className="text-slate1 shrink-0 text-xs">{(ev.start_dt || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attributi per categoria */}
      <div className="mt-5">
        <div className="text-[11px] tracking-[0.18em] uppercase text-slate1 mb-2">Dati raccolti</div>
        {Object.keys(grouped).length === 0 ? (
          <div className="text-sm text-slate-400">Nessun dato ancora. Usa il pulsante <span className="font-medium text-ink">modifica</span> (matita) per compilare le categorie, oppure registra una nota vocale.</div>
        ) : (
          <div className="space-y-2">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat} className="flex gap-3" data-testid={`si-attr-${cat}`}>
                <div className="w-40 shrink-0 text-xs tracking-wider uppercase text-gold pt-0.5">{cat}</div>
                <div className="flex-1 text-sm text-ink space-y-0.5">
                  {items.map((it, i) => <div key={i}>{it.value} {it.source === "voice" && <span className="text-[9px] text-slate1 uppercase ml-1">voce</span>}</div>)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Timeline note vocali */}
      {data.recordings?.length > 0 && (
        <div className="mt-6">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate1 mb-2">Note vocali ({data.recordings.length})</div>
          <div className="space-y-2">
            {data.recordings.map((r) => (
              <div key={r.id} className="bg-cream/40 border border-ink/10 rounded-sm px-3 py-2" data-testid={`si-rec-${r.id}`}>
                <div className="text-[10px] text-slate1 mb-0.5">{r.day} · {r.time}{r.admin_name ? ` · ${r.admin_name}` : ""} {r.audio_deleted && "· audio eliminato"}</div>
                <div className="text-sm text-ink/80">{r.transcript}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ icon, label, value, alert }) {
  return (
    <div className={`rounded-sm p-2 text-center ${alert ? "bg-rose-50" : "bg-cream/40"}`}>
      <div className="text-[9px] tracking-wider uppercase text-slate1 inline-flex items-center gap-1">{icon}{label}</div>
      <div className={`text-sm font-semibold mt-0.5 tabular-nums ${alert ? "text-rose-600" : "text-ink"}`}>{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.15em] uppercase text-slate1">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

/* ---------------- Modale proposta (conferma nota) ---------------- */
function ProposalModal({ proposal, categories = [], onClose, onConfirmed }) {
  const isEmailCat = (c) => /mail/i.test((c || "").trim());
  const [attrs, setAttrs] = useState(() => (proposal.attributes || []).filter((a) => !isEmailCat(a.category)).map((a) => ({ ...a, keep: true })));
  const [mode, setMode] = useState(() => proposal.candidates?.length ? "existing" : "new");
  const [clientId, setClientId] = useState(() => proposal.candidates?.[0]?.client_id || "");
  const [newName, setNewName] = useState(proposal.suggested_new_name || "");
  const [phone, setPhone] = useState(proposal.contact?.phone || "");
  const [email, setEmail] = useState(() => proposal.contact?.email || (proposal.attributes || []).find((a) => isEmailCat(a.category))?.value || "");
  const [address, setAddress] = useState(proposal.contact?.address || "");
  const [search, setSearch] = useState([]);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);

  // Dati già presenti nella scheda selezionata: così si vede cosa manca ed si evitano duplicati
  useEffect(() => {
    if (mode !== "existing" || !clientId) { setExisting(null); return; }
    let alive = true;
    (async () => {
      try { const { data } = await api.get(`/admin/store-insight/clients/${clientId}`); if (alive) setExisting(data.client); }
      catch { if (alive) setExisting(null); }
    })();
    return () => { alive = false; };
  }, [mode, clientId]);

  // Categorie già censite non ancora presenti (né tra i dati estratti né già in scheda) → evita duplicati
  const usedCats = new Set([
    ...attrs.map((a) => (a.category || "").trim().toLowerCase()),
    ...((existing?.attributes || []).map((a) => (a.category || "").trim().toLowerCase())),
  ]);
  const suggestCats = (categories || []).filter((c) => c.label && !isEmailCat(c.label) && !usedCats.has(c.label.trim().toLowerCase()));

  function addCategory(label) {
    setAttrs((p) => [...p, { category: label, value: "", keep: true }]);
  }

  async function doSearch(term) {
    if (!term.trim()) { setSearch([]); return; }
    try { const { data } = await api.get("/admin/store-insight/clients", { params: { q: term } }); setSearch(data.slice(0, 6)); } catch { /* */ }
  }

  async function confirm() {
    const chosen = attrs.filter((a) => a.keep && (a.category || "").trim() && (a.value || "").trim()).map((a) => ({ category: a.category, value: a.value }));
    const body = { attributes: chosen, phone: phone.trim(), email: email.trim(), address: address.trim() };
    if (mode === "new") {
      if (!newName.trim()) { toast.error("Indica il nome della nuova scheda"); return; }
      body.new_client_name = newName.trim();
    } else {
      if (!clientId) { toast.error("Seleziona un cliente"); return; }
      body.client_id = clientId;
    }
    setSaving(true);
    try {
      await api.post(`/admin/store-insight/record/${proposal.recording_id}/confirm`, body);
      toast.success("Nota confermata e dati aggiunti alla scheda");
      onConfirmed();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore conferma"); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="si-proposal-modal">
      <div className="bg-cream w-full max-w-2xl max-h-[90vh] overflow-auto rounded-sm shadow-2xl">
        <div className="sticky top-0 bg-cream border-b border-ink/10 px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-sm tracking-[0.18em] uppercase text-ink font-semibold">Conferma nota vocale</div>
            {(proposal.admin_name || proposal.day) && <div className="text-[10px] text-slate1 mt-0.5">{proposal.day} {proposal.time}{proposal.admin_name ? ` · registrata da ${proposal.admin_name}` : ""}</div>}
          </div>
          <button onClick={onClose} className="p-1 text-slate1 hover:text-ink" data-testid="si-proposal-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 mb-1">Trascrizione</div>
            <div className="bg-white border border-ink/10 rounded-sm p-3 text-sm text-ink/80 max-h-32 overflow-auto">{proposal.transcript}</div>
          </div>

          {/* Scelta cliente */}
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 mb-2">A quale cliente?</div>
            <div className="flex gap-2 mb-2">
              <button onClick={() => setMode("existing")} className={`flex-1 py-2 rounded-sm text-xs tracking-wider uppercase border ${mode === "existing" ? "bg-ink text-cream border-ink" : "border-ink/20 text-ink"}`} data-testid="si-mode-existing">Cliente esistente</button>
              <button onClick={() => setMode("new")} className={`flex-1 py-2 rounded-sm text-xs tracking-wider uppercase border inline-flex items-center justify-center gap-1 ${mode === "new" ? "bg-ink text-cream border-ink" : "border-ink/20 text-ink"}`} data-testid="si-mode-new"><UserPlus size={13} /> Nuova scheda</button>
            </div>
            {mode === "existing" ? (
              <div className="space-y-1.5">
                {proposal.candidates?.length > 0 && proposal.candidates.map((c) => (
                  <label key={c.client_id} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-sm border cursor-pointer ${clientId === c.client_id ? "border-gold bg-gold/10" : "border-ink/15 bg-white"}`} data-testid={`si-cand-${c.client_id}`}>
                    <span className="text-sm text-ink inline-flex items-center gap-2"><input type="radio" checked={clientId === c.client_id} onChange={() => setClientId(c.client_id)} /> {c.name}</span>
                    <span className="text-[10px] text-slate1 uppercase">corrispondenza {Math.round(c.score * 100)}%</span>
                  </label>
                ))}
                <div className="relative mt-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" />
                  <input placeholder="Cerca un altro cliente…" onChange={(e) => doSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-ink/20 rounded-sm text-sm bg-white" data-testid="si-cand-search" />
                </div>
                {search.map((c) => (
                  <label key={c.id} className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer ${clientId === c.id ? "border-gold bg-gold/10" : "border-ink/15 bg-white"}`}>
                    <input type="radio" checked={clientId === c.id} onChange={() => setClientId(c.id)} /> <span className="text-sm text-ink">{c.name}</span>
                  </label>
                ))}
              </div>
            ) : (
              <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nome nuovo cliente (rilevato dalla voce)" className="w-full px-3 py-2 border border-ink/20 rounded-sm text-sm bg-white" data-testid="si-new-name" />
            )}
            {/* Contatti rilevati dalla voce (completano la scheda se mancanti) */}
            <div className="grid sm:grid-cols-3 gap-2 mt-2">
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefono (dalla voce)" className="w-full px-3 py-2 border border-ink/20 rounded-sm text-sm bg-white" data-testid="si-contact-phone" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (dalla voce)" className="w-full px-3 py-2 border border-ink/20 rounded-sm text-sm bg-white" data-testid="si-contact-email" />
              <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Indirizzo (dalla voce)" className="w-full px-3 py-2 border border-ink/20 rounded-sm text-sm bg-white" data-testid="si-contact-address" />
            </div>
            {/* Dati già presenti nella scheda selezionata (per evitare duplicati) */}
            {mode === "existing" && existing && (
              <div className="mt-3 bg-cream2/50 border border-ink/10 rounded-sm p-3" data-testid="si-existing-data">
                <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 mb-2">Già presente in scheda · {existing.name}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink/80 mb-2">
                  <span className="inline-flex items-center gap-1"><Phone size={11} className={existing.phone ? "text-gold" : "text-slate1/40"} /> {existing.phone || <span className="text-slate1/60 italic">mancante</span>}</span>
                  <span className="inline-flex items-center gap-1"><Mail size={11} className={existing.email ? "text-gold" : "text-slate1/40"} /> {existing.email || <span className="text-slate1/60 italic">mancante</span>}</span>
                  <span className="inline-flex items-center gap-1"><MapPin size={11} className={existing.address ? "text-gold" : "text-slate1/40"} /> {existing.address || <span className="text-slate1/60 italic">mancante</span>}</span>
                </div>
                {(existing.attributes || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {existing.attributes.map((a, i) => (
                      <span key={i} className="text-[11px] bg-white border border-ink/10 rounded px-2 py-0.5 text-ink" data-testid={`si-existing-attr-${i}`}>
                        <span className="text-slate1 uppercase tracking-wider mr-1">{a.category}:</span>{a.value}
                      </span>
                    ))}
                  </div>
                ) : <div className="text-[11px] text-slate1/70 italic">Nessun dato raccolto finora.</div>}
              </div>
            )}
          </div>

          {/* Attributi estratti */}
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 mb-2">Dati estratti dall'AI ({attrs.filter((a) => a.keep).length}/{attrs.length})</div>
            {attrs.length === 0 && <div className="text-sm text-slate-400">Nessun dato estratto da questa nota.</div>}
            <div className="space-y-1.5">
              {attrs.map((a, i) => (
                <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-sm border ${a.keep ? "border-ink/15 bg-white" : "border-ink/10 bg-cream2/40 opacity-60"}`} data-testid={`si-attr-row-${i}`}>
                  <input type="checkbox" checked={a.keep} onChange={(e) => setAttrs((p) => p.map((x, j) => j === i ? { ...x, keep: e.target.checked } : x))} />
                  <input value={a.category} onChange={(e) => setAttrs((p) => p.map((x, j) => j === i ? { ...x, category: e.target.value } : x))} className="w-40 shrink-0 text-xs tracking-wider uppercase text-gold bg-transparent border-b border-dashed border-ink/20 focus:outline-none" />
                  <input value={a.value} onChange={(e) => setAttrs((p) => p.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} className="flex-1 text-sm text-ink bg-transparent border-b border-dashed border-ink/20 focus:outline-none" />
                </div>
              ))}
            </div>
            <button onClick={() => setAttrs((p) => [...p, { category: "", value: "", keep: true }])} className="mt-2 text-xs text-ink inline-flex items-center gap-1 hover:underline" data-testid="si-attr-add"><Plus size={12} /> Aggiungi dato</button>
            {suggestCats.length > 0 && (
              <div className="mt-3" data-testid="si-cat-suggestions">
                <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 mb-1.5">Categorie già censite — tocca per completare</div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestCats.map((c) => (
                    <button key={c.key || c.label} onClick={() => addCategory(c.label)} className="inline-flex items-center gap-1 text-[11px] text-ink border border-ink/20 bg-white hover:border-gold hover:bg-gold/10 rounded-full px-2.5 py-1 transition-colors" data-testid={`si-cat-chip-${c.key || c.label}`}>
                      <Plus size={10} /> {c.label}{c.count ? <span className="text-slate1">· {c.count}</span> : null}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="sticky bottom-0 bg-cream border-t border-ink/10 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate1 hover:text-ink">Annulla</button>
          <button onClick={confirm} disabled={saving} className="px-4 py-2 rounded-sm bg-ink text-cream text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2 disabled:opacity-50" data-testid="si-proposal-confirm">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Conferma e salva
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Modale link mobile ---------------- */
function LinkModal({ link, onClose, onRotate }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="si-link-modal">
      <div className="bg-cream w-full max-w-sm rounded-sm shadow-2xl p-6 text-center">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm tracking-[0.18em] uppercase text-ink font-semibold inline-flex items-center gap-2"><QrCode size={16} /> Link registrazione</div>
          <button onClick={onClose} className="p-1 text-slate1 hover:text-ink"><X size={18} /></button>
        </div>
        <div className="bg-white p-4 rounded-sm inline-block"><QRCodeSVG value={link.url} size={180} /></div>
        <p className="text-xs text-slate1 mt-4">Inquadra il QR col cellulare (anche iPhone) oppure copia il link. Funziona come web app per registrare note vocali sui clienti.</p>
        <div className="mt-3 flex items-center gap-2">
          <input readOnly value={link.url} className="flex-1 text-xs px-2 py-2 border border-ink/20 rounded-sm bg-white truncate" data-testid="si-link-url" />
          <button onClick={() => { try { navigator.clipboard.writeText(link.url); toast.success("Link copiato"); } catch { toast.error("Copia non riuscita: seleziona e copia manualmente"); } }} className="px-3 py-2 bg-ink text-cream text-xs rounded-sm">Copia</button>
        </div>
        <button onClick={onRotate} className="mt-3 text-[11px] text-slate1 hover:text-ink underline" data-testid="si-link-rotate">Genera nuovo link (disattiva il precedente)</button>
      </div>
    </div>
  );
}
